"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type CartItem = {
  productId: string;
  productVariantId: string | null;
  quantity: number;
  productName: string;
  variantName: string | null;
  unitPriceCents: number;
};

type CartContextValue = {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "quantity"> & { quantity?: number }) => void;
  removeItem: (productId: string, productVariantId: string | null) => void;
  updateQuantity: (productId: string, productVariantId: string | null, quantity: number) => void;
  totalCents: number;
  totalCount: number;
  clearCart: () => void;
};

const CartContext = createContext<CartContextValue | null>(null);

const STORAGE_KEY_PREFIX = "book4_cart_";

function loadCart(slug: string): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + slug);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CartItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveCart(slug: string, items: CartItem[]) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + slug, JSON.stringify(items));
  } catch {
    // ignore
  }
}

export function CartProvider({
  businessSlug,
  children,
}: {
  businessSlug: string | null;
  children: React.ReactNode;
}) {
  const [items, setItems] = useState<CartItem[]>(() =>
    businessSlug ? loadCart(businessSlug) : []
  );

  const addItem = useCallback(
    (item: Omit<CartItem, "quantity"> & { quantity?: number }) => {
      if (!businessSlug) return;
      const qty = Math.max(1, item.quantity ?? 1);
      const key = (id: string, vid: string | null) => `${id}:${vid ?? ""}`;
      const itemKey = key(item.productId, item.productVariantId);
      setItems((prev) => {
        const idx = prev.findIndex(
          (i) => key(i.productId, i.productVariantId) === itemKey
        );
        const next =
          idx >= 0
            ? prev.map((i, iidx) =>
                iidx === idx ? { ...i, quantity: i.quantity + qty } : i
              )
            : [...prev, { ...item, quantity: qty } as CartItem];
        saveCart(businessSlug, next);
        return next;
      });
    },
    [businessSlug]
  );

  const removeItem = useCallback(
    (productId: string, productVariantId: string | null) => {
      if (!businessSlug) return;
      setItems((prev) => {
        const next = prev.filter(
          (i) =>
            !(i.productId === productId && i.productVariantId === productVariantId)
        );
        saveCart(businessSlug, next);
        return next;
      });
    },
    [businessSlug]
  );

  const updateQuantity = useCallback(
    (productId: string, productVariantId: string | null, quantity: number) => {
      if (!businessSlug) return;
      if (quantity < 1) {
        removeItem(productId, productVariantId);
        return;
      }
      setItems((prev) => {
        const next = prev.map((i) =>
          i.productId === productId && i.productVariantId === productVariantId
            ? { ...i, quantity }
            : i
        );
        saveCart(businessSlug, next);
        return next;
      });
    },
    [businessSlug, removeItem]
  );

  const clearCart = useCallback(() => {
    setItems((prev) => {
      console.log("[CartContext] clearCart called", { businessSlug, itemsCount: prev.length });
      return [];
    });
    if (businessSlug) saveCart(businessSlug, []);
  }, [businessSlug]);

  const totalCents = useMemo(
    () => items.reduce((sum, i) => sum + i.unitPriceCents * i.quantity, 0),
    [items]
  );
  const totalCount = useMemo(
    () => items.reduce((sum, i) => sum + i.quantity, 0),
    [items]
  );

  const value: CartContextValue = useMemo(
    () => ({
      items,
      addItem,
      removeItem,
      updateQuantity,
      totalCents,
      totalCount,
      clearCart,
    }),
    [items, addItem, removeItem, updateQuantity, totalCents, totalCount, clearCart]
  );

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

// Disable adding add-ons while on the payment step (so cart can't change after submit)
type AddonsDisabledContextValue = {
  addonsDisabled: boolean;
  setAddonsDisabled: (disabled: boolean) => void;
};

const AddonsDisabledContext = createContext<AddonsDisabledContextValue>({
  addonsDisabled: false,
  setAddonsDisabled: () => {},
});

export function AddonsDisabledProvider({
  children,
  onReady,
}: {
  children: React.ReactNode;
  /** Called with the setter so parent can pass it to BookingForm */
  onReady?: (setAddonsDisabled: (disabled: boolean) => void) => void;
}) {
  const [addonsDisabled, setAddonsDisabled] = useState(false);
  const value = useMemo(
    () => ({ addonsDisabled, setAddonsDisabled }),
    [addonsDisabled]
  );
  useEffect(() => {
    onReady?.(setAddonsDisabled);
  }, [onReady, setAddonsDisabled]);
  return (
    <AddonsDisabledContext.Provider value={value}>{children}</AddonsDisabledContext.Provider>
  );
}

export function useAddonsDisabled(): AddonsDisabledContextValue {
  return useContext(AddonsDisabledContext);
}
